import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { usePDF } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import usePlanServiceApi from 'hooks/usePlanServiceApi';
import useCropSeasonServiceApi from 'hooks/useCropSeasonServiceApi';
import {
  PLAN_FETCH_V2,
  PLAN_FETCH_V2_BY_ORG
} from 'constants/planServiceV2Flags';
import {
  CROP_SEASON_FETCH_V2,
  CROP_SEASON_FETCH_V2_BY_ORG
} from 'constants/cropSeasonServiceV2Flags';
import { getPropertyExtraData } from 'components/PDF/helpers/pdfTools';
import PlanPdf from 'components/PDF/PlanPdf';
import usePDFTools from 'hooks/usePDFTools';
import axios from 'axios';
import { MASTERLIST_CROPS_URL } from 'utilities/apiConstants';
import { getAccessToken } from 'utilities/base-auth';
import { Context } from 'components/Store';
import {
  fieldsToTable,
  fixedCostsToTable,
  mapProductsToTable
} from 'components/PDF/PlanPdf/helpers/tableMaker';
import { sumFieldAcres } from 'components/PDF/helpers/tableGenerator';
import { paths } from 'routes/paths';
import { OperationType } from 'components/RecordsListView/constants';
import { message } from 'antd';
import { RECORD_SOURCE_MESSAGE_KEY } from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/DetailsTab/components/RecordSourceButton/constants/messageKey';
import usePlanToOperationAction from 'screens/Plans/hooks/usePlanToOperationAction';
import { RecordSourceType } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import usePlansFlags from 'screens/Plans/redesign/hooks/usePlansFlags';
import {
  getPlanFixedCost,
  getPlanProjNetRevenue,
  getPlanProjNetRevenuePerArea,
  getVarCost
} from '../components/PlanView/helpers';

const usePlanEllipsisOptions = currentPlan => {
  const history = useHistory();
  const amplitude = useContext(AmplitudeContext);
  const { fePlansActive } = usePlansFlags();
  const [{ organization }] = useContext(Context);
  const plansAPI = usePlanServiceApi(
    'plan',
    PLAN_FETCH_V2,
    PLAN_FETCH_V2_BY_ORG
  );
  const cropSeasonFetchApi = useCropSeasonServiceApi(
    'cropseason',
    CROP_SEASON_FETCH_V2,
    CROP_SEASON_FETCH_V2_BY_ORG
  );
  const { downloadPDFV2, downloading, setDownloading } = usePDFTools();

  const [planToPdf, setPlanToPdf] = useState();
  const [loadingTags, setLoadingTags] = useState(false);

  // Held in a variable so the same element can be handed to updateInstance
  // below: usePDF only reads `document` on mount, and its updater renders
  // whatever it is passed rather than re-reading this prop.
  const pdfDocument = <PlanPdf plan={planToPdf} />;

  const [instance, updateInstance] = usePDF({ document: pdfDocument });
  const [currentOpenModal, setCurrentOpenModal] = useState('');
  const [selectedOperationType, setSelectedOperationType] = useState(
    OperationType.APPLICATION
  );

  const prevPdfUrl = useRef(/** @type {string|undefined} */ (undefined));

  const {
    handleConvertPlanToOperationAction,
    handleConvertPlanToRecommendationAction
  } = usePlanToOperationAction();

  const openConvertToOperationModal = operationType => {
    setSelectedOperationType(operationType);
    setCurrentOpenModal('convertToOperation');
  };

  useEffect(() => {
    if (planToPdf) {
      updateInstance(pdfDocument);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planToPdf]);

  useEffect(() => {
    if (
      !instance.loading &&
      instance.url &&
      prevPdfUrl.current !== instance.url &&
      planToPdf
    ) {
      prevPdfUrl.current = instance.url;
      downloadPDFV2(prevPdfUrl.current, `Plan - ${planToPdf.name}.pdf`);
      setPlanToPdf(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance]);

  // `PlanView`/`PlanTable`/`PlanTileBoard` (the only other callers of this
  // hook) only render when `fePlansActive` is off, so the flag branch below
  // is reachable only from the redesigned `PlanActionMenu`.
  const edit = id => {
    if (fePlansActive) {
      history.push(`${paths.plans}/${id}/edit`);
      return;
    }
    history.push({
      pathname: `${paths.plans}/${id}`,
      selectedView: 'edit-fromView'
    });
  };

  const duplicate = id => {
    if (fePlansActive) {
      history.push({
        pathname: `${paths.plans}/create`,
        state: { flow: 'duplicate', sourcePlanId: id }
      });
      return;
    }
    history.push({
      pathname: `${paths.plans}/${id}`,
      state: {
        selectedView: 'edit-dup'
      }
    });
  };

  const deleteOpt = () => {
    setCurrentOpenModal('delete');
    amplitude.sendEventToAmplitude(amplitude.events.epic.Plans.deletePlan);
  };

  const shareOrPrint = async id => {
    setDownloading(true);

    const getCrop = async cropId => {
      const cropUrl = `${MASTERLIST_CROPS_URL}/crops/${cropId}?location=@CWF`;
      const { data: crop } = await axios
        .get(cropUrl, {
          headers: { common: { Authorization: `Bearer ${getAccessToken()}` } }
        })
        .catch(() => ({}));

      return crop;
    };

    const { promise } = plansAPI.fetch(id);
    const { data: plan } = await promise.catch(() => ({}));

    if (plan) {
      const mappedCropZonesPromise = plan.cropZones.map(async cropZone => {
        const {
          cropZone: cropZoneInfo,
          field,
          farm,
          crop
        } = await getPropertyExtraData(cropZone.cropZoneIdValue);

        return {
          ...cropZone,
          farmName: farm.name,
          fieldName: field.name,
          cropZoneName: cropZoneInfo.name,
          cropName: crop.name
        };
      });

      const cropSeasonPromises = plan.cropSeasonIds.map(async cropSeasonId => {
        const { promise: cropSeasonPromise } = cropSeasonFetchApi.fetch(
          cropSeasonId,
          undefined,
          undefined,
          undefined,
          true // ignoreCache
        );

        return cropSeasonPromise;
      });

      const promises = await Promise.allSettled([
        ...mappedCropZonesPromise,
        ...cropSeasonPromises,
        getCrop(plan.cropId)
      ]);

      const cropZonesLength = mappedCropZonesPromise.length;
      const cropZonesPlusSeasons = cropZonesLength + cropSeasonPromises.length;

      const mappedCropZones = promises.slice(0, cropZonesLength);
      const cropSeasons = promises.slice(cropZonesLength, cropZonesPlusSeasons);
      const crop = promises.slice(-1);
      const areaSum = sumFieldAcres(plan.cropZones);
      const totalCost = getVarCost(plan);
      const projNetRevPerArea = getPlanProjNetRevenuePerArea(plan);
      const projNetRev = getPlanProjNetRevenue(plan);
      const generatedDate = new Date();
      const year = format(generatedDate, 'yyyy');
      const fullDate = {
        english: format(generatedDate, 'MM/dd/yyyy h:mm aaa'),
        LATAMSpanish: format(generatedDate, 'dd/MM/yyyy h:mm aaaa')
      };
      const totalFixedCost = getPlanFixedCost(plan);

      setPlanToPdf({
        ...plan,
        cropZones: fieldsToTable(
          mappedCropZones
            .filter(request => request.status === 'fulfilled')
            .map(request => request.value)
        ),
        organizationName: organization.name,
        cropSeasons: cropSeasons
          .filter(request => request.status === 'fulfilled')
          .map(request => request.value.data.name),
        cropName: crop[0].value?.name,
        planProductEntries: mapProductsToTable(plan.planProductEntries),
        fixedCosts: fixedCostsToTable(plan, totalFixedCost),
        areaSum,
        totalCost,
        costPerArea: totalCost / plan.estimatedAreaValue,
        projNetRevPerArea,
        projNetRev,
        year,
        fullDate,
        totalFixedCost
      });
    }

    setDownloading(false);
  };

  const handleAction = (action, id = null) => {
    const actions = {
      edit,
      tag: () => setCurrentOpenModal('tag'),
      duplicate,
      convertToRec: () =>
        handleConvertPlanToRecommendationAction(currentPlan, () =>
          setCurrentOpenModal('convertToRec')
        ),
      createRecommendation: () =>
        handleConvertPlanToRecommendationAction(currentPlan, () =>
          setCurrentOpenModal('convertToRec')
        ),
      convertToApplication: () => {
        handleConvertPlanToOperationAction(
          currentPlan,
          OperationType.APPLICATION,
          RecordSourceType.PLANS,
          () => {
            openConvertToOperationModal(OperationType.APPLICATION);
          }
        );
      },
      createWorkOrder: () => {
        handleConvertPlanToOperationAction(
          currentPlan,
          OperationType.WORK_ORDER,
          RecordSourceType.PLANS,
          () => {
            openConvertToOperationModal(OperationType.WORK_ORDER);
          }
        );
      },
      shareOrPrint,
      downloadPDFEnglish: shareOrPrint,
      delete: deleteOpt
    };

    message.destroy(RECORD_SOURCE_MESSAGE_KEY);

    actions[action](id);
  };

  return {
    handleAction,
    currentOpenModal,
    setCurrentOpenModal,
    selectedOperationType,
    downloadingPDF: downloading,
    shareOrPrint,
    setLoadingTags,
    loadingTags
  };
};

usePlanEllipsisOptions.propTypes = {
  currentPlan: PropTypes.shape({})
};

export default usePlanEllipsisOptions;
